/**
 * Compliance Dry-Run Demo Page.
 *
 * One-button "Run Italian compliance demo" that drives a headless
 * Chromium across our local AlloggiatiWeb mock and reports back the
 * timing + receipt + 6 screenshots. Designed to be a sales artifact
 * for the Italian-pilot conversation: "watch Marina submit a guest's
 * identity to the police portal in 2 seconds."
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Play, Clock, CheckCircle2, XCircle,
  Image as ImageIcon, ChevronDown, ChevronRight, Trash2, FileText,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/backendUrl';

const DEFAULT_GUEST = {
  last_name: 'Rossi',
  first_name: 'Marco',
  date_of_birth: '1991-04-12',
  gender: 'M',
  place_of_birth: 'Milano',
  nationality: 'IT',
  document_type: 'PASSAPORTO',
  document_number: 'YA1234567',
  document_issuing_country: 'IT',
  check_in_date: '2026-06-15',
  nights: 2,
  group_type: 'Singolo',
};

function fmtRelative(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function ComplianceDryRunPage() {
  const nav = useNavigate();
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openRun, setOpenRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [showCustom, setShowCustom] = useState(false);
  const [guest, setGuest] = useState(DEFAULT_GUEST);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/compliance/dry-run/italy/runs?limit=50`);
      const j = await r.json();
      setRuns(j.runs || []);
    } catch {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const fireRun = async (customGuest = null) => {
    setRunning(true);
    setActionMsg(null);
    try {
      const body = customGuest ? { guest_data: customGuest } : {};
      const r = await fetch(`${API}/api/compliance/dry-run/italy/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || 'run failed');
      setActionMsg({
        type: 'ok',
        text: `Run ${j.id} completed in ${j.total_seconds}s · receipt ${j.receipt_number}`,
      });
      setOpenRun(j.id);
      await load();
    } catch (e) {
      setActionMsg({ type: 'err', text: e.message });
    }
    setRunning(false);
  };

  const deleteRun = async (id) => {
    if (!window.confirm(`Delete run ${id} and its screenshots?`)) return;
    try {
      await fetch(`${API}/api/compliance/dry-run/italy/runs/${id}`, { method: 'DELETE' });
      await load();
    } catch {}
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100">
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => nav('/admin')} data-testid="cdr-back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                🇮🇹 Italian Compliance · Dry-Run Demo
              </h1>
              <p className="text-xs text-zinc-400">
                Headless Chromium drives a local mock of AlloggiatiWeb · for Italian-pilot pitches · zero external dependency
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={load} data-testid="cdr-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Action card */}
        <Card className="bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 border-zinc-800 mb-5">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="text-lg font-semibold text-zinc-100 mb-1">
                  Run the demo
                </div>
                <div className="text-xs text-zinc-400 mb-3">
                  Drives a fresh headless Chromium against{' '}
                  <a href="/mock-portal/alloggiati/login.html" target="_blank" rel="noreferrer" className="text-amber-300 hover:text-amber-200 underline decoration-dotted">
                    our local AlloggiatiWeb replica
                  </a>
                  {' '}— ~2 seconds end-to-end, 6 screenshots, realistic receipt number. Default sample is Marco Rossi from Milano.
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => fireRun()}
                  disabled={running}
                  className="bg-emerald-600 hover:bg-emerald-500"
                  data-testid="cdr-run-default-btn"
                >
                  <Play className="w-4 h-4 mr-1" /> {running ? 'Running…' : 'Run with default guest'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowCustom(!showCustom)}
                  className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  data-testid="cdr-toggle-custom-btn"
                >
                  Custom guest data {showCustom ? '↑' : '↓'}
                </Button>
              </div>
            </div>

            {showCustom && (
              <div className="mt-4 pt-4 border-t border-zinc-800 grid grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(DEFAULT_GUEST).map(([k, defVal]) => (
                  <div key={k}>
                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-0.5 block">{k}</label>
                    <input
                      type={k === 'date_of_birth' || k === 'check_in_date' ? 'date' : (k === 'nights' ? 'number' : 'text')}
                      value={guest[k] ?? defVal}
                      onChange={(e) => setGuest({ ...guest, [k]: k === 'nights' ? Number(e.target.value) : e.target.value })}
                      className="w-full bg-zinc-950 border border-zinc-800 rounded px-2 py-1 text-xs"
                      data-testid={`cdr-guest-${k}-input`}
                    />
                  </div>
                ))}
                <div className="col-span-2 md:col-span-3 flex justify-end mt-1">
                  <Button
                    size="sm"
                    onClick={() => fireRun(guest)}
                    disabled={running}
                    className="bg-amber-600 hover:bg-amber-500"
                    data-testid="cdr-run-custom-btn"
                  >
                    <Play className="w-4 h-4 mr-1" /> Run with custom data
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {actionMsg && (
          <div
            className={`mb-3 px-3 py-2 rounded text-sm ${
              actionMsg.type === 'ok'
                ? 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/30'
                : 'bg-rose-500/10 text-rose-300 border border-rose-500/30'
            }`}
            data-testid="cdr-action-msg"
          >
            {actionMsg.text}
          </div>
        )}

        {/* Runs list */}
        {loading ? (
          <div className="text-zinc-500 py-12 text-center">Loading…</div>
        ) : runs.length === 0 ? (
          <Card className="bg-zinc-900/40 border-zinc-800">
            <CardContent className="py-10 text-center text-zinc-500">
              No runs yet. Hit "Run with default guest" above to produce the first demo.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {runs.map((r) => (
              <RunRow
                key={r.id}
                r={r}
                open={openRun === r.id}
                onToggle={() => setOpenRun(openRun === r.id ? null : r.id)}
                onDelete={() => deleteRun(r.id)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function RunRow({ r, open, onToggle, onDelete }) {
  const ok = r.status === 'completed';
  const fail = r.status === 'failed';
  return (
    <Card className="bg-zinc-900/60 border-zinc-800" data-testid={`cdr-row-${r.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <button onClick={onToggle} className="text-zinc-500 hover:text-zinc-200 mt-1" data-testid={`cdr-toggle-${r.id}-btn`}>
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {ok && <CheckCircle2 className="w-4 h-4 text-emerald-400" />}
              {fail && <XCircle className="w-4 h-4 text-rose-400" />}
              {!ok && !fail && <Clock className="w-4 h-4 text-blue-400" />}
              <Badge className={ok ? 'bg-emerald-500/15 text-emerald-300' : fail ? 'bg-rose-500/15 text-rose-300' : 'bg-blue-500/15 text-blue-300'}>
                {r.status}
              </Badge>
              {ok && (
                <>
                  <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700">
                    <Clock className="w-3 h-3 mr-1" /> {r.total_seconds}s
                  </Badge>
                  <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 font-mono">
                    {r.receipt_number}
                  </Badge>
                </>
              )}
              <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700">
                <ImageIcon className="w-3 h-3 mr-1" /> {(r.screenshots || []).length} screenshots
              </Badge>
            </div>
            <div className="text-sm text-zinc-200">
              {r.guest_data?.first_name} {r.guest_data?.last_name}
              <span className="text-zinc-500"> · {r.guest_data?.nationality}</span>
              <span className="text-zinc-500"> · {r.guest_data?.document_type} {r.guest_data?.document_number}</span>
            </div>
            <div className="text-[11px] text-zinc-500 mt-0.5">
              id <span className="font-mono">{r.id}</span> · {fmtRelative(r.created_at)}
            </div>
            {r.error && (
              <div className="mt-2 bg-rose-500/5 border border-rose-500/20 rounded p-2 text-xs text-rose-200">
                {r.error}
              </div>
            )}
          </div>
          <div>
            <Button
              size="sm"
              variant="outline"
              onClick={onDelete}
              className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
              data-testid={`cdr-delete-${r.id}-btn`}
            >
              <Trash2 className="w-4 h-4 mr-1" /> Delete
            </Button>
          </div>
        </div>

        {open && (
          <div className="mt-4 pt-4 border-t border-zinc-800/80">
            {/* Screenshot strip */}
            <div className="text-zinc-500 text-xs mb-2 flex items-center gap-1">
              <FileText className="w-3 h-3" /> Step-by-step trajectory
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {(r.screenshots || []).map((s) => (
                <a
                  key={s.step}
                  href={s.path}
                  target="_blank"
                  rel="noreferrer"
                  className="bg-black/40 border border-zinc-800 rounded overflow-hidden hover:border-amber-500/40 transition"
                  data-testid={`cdr-screenshot-${r.id}-${s.step}`}
                >
                  <img src={s.path} alt={`step ${s.step}`} className="w-full h-auto block" />
                  <div className="p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="text-amber-300 font-mono">step {s.step}</span>
                      <span className="text-zinc-500">{s.elapsed_ms}ms</span>
                    </div>
                    <div className="text-zinc-200 mt-1">{s.label}</div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
